import { useCloudApi } from '@/api/cloudApi'
import type { V1alpha1Secret } from '@streamnative/cloud-api-client-typescript'
import { i18n } from '@/lang'
import { useInstance } from '@/composables/useInstance'
import {
  getSecretDataKeys,
  splitSecretDataEntries,
  type SecretDataEntry,
  type SplitSecretDataEntriesResult
} from '@/utils/secretData'
import type { PulsarState } from './usePulsarState'

export type V1alpha1SecretWithBinaryData = V1alpha1Secret & {
  binaryData?: Record<string, string>
}

const { t } = i18n.global
const { instances } = useInstance()

const secrets = ref<Secret[]>([])
let lastOrg: string | undefined = undefined
export interface Secret {
  name: string
  creationTimestamp?: string
  deletionTimestamp?: string
  instanceName?: string
  location?: string
  poolMemberRef?: {
    name: string
    namespace: string
  }
  keys?: string[]
  data?: Record<string, unknown>
  binaryData?: Record<string, unknown>
}

const removeSecret = (name: string) => {
  secrets.value.forEach((s, i) => {
    if (s.name === name) {
      secrets.value[i].deletionTimestamp = new Date().toISOString()
    }
  })
}

const addSecret = (secret: Secret) => {
  secrets.value = [secret, ...secrets.value]
}

const hasSecrets = computed(() => {
  return secrets.value.length > 0
})
const secretNameList = computed(() => {
  return secrets.value.map(secret => secret.name)
})

const getSecrets = async (organization?: string | undefined) => {
  if (!organization) {
    organization = usePulsarState().mustOrganization()
  }
  const { data } = await useCloudApi().listNamespacedSecret(organization)

  secrets.value = data.items.map(secret => {
    const secretWithBinaryData = secret as V1alpha1SecretWithBinaryData
    return {
      name: secret.metadata?.name ?? '',
      creationTimestamp: secret.metadata?.creationTimestamp,
      deletionTimestamp: secret.metadata?.deletionTimestamp,
      instanceName: secret.instanceName,
      location: secret.location,
      poolMemberRef: secret.poolMemberRef,
      keys: getSecretDataKeys(secret.data, secretWithBinaryData.binaryData),
      data: secret.data || {},
      binaryData: secretWithBinaryData.binaryData || {}
    } as Secret
  })
}

const getSecret = async (name: string, organization?: string | undefined) => {
  const { mustOrganization } = usePulsarState()
  const { data } = await useCloudApi().readNamespacedSecret(
    name,
    organization ?? mustOrganization()
  )
  return data
}

const createSecret = async ({
  location,
  name,
  data,
  token,
  clientId,
  clientSecret,
  credential,
  catalog,
  instanceName
}: {
  location?: string
  name: string
  data?: SecretDataEntry[]
  token?: string
  clientId?: string
  clientSecret?: string
  credential?: string
  catalog?: boolean
  instanceName?: string
}) => {
  try {
    const organization = usePulsarState().mustOrganization()
    const selectedInstance = instances.value.find(i => i.metadata?.name === instanceName)

    const dataList: Record<string, string> = {}
    const secretData: SplitSecretDataEntriesResult = catalog
      ? { data: {}, binaryData: {} }
      : splitSecretDataEntries(data)
    const ownerReferences = []
    if (credential !== undefined && credential !== '') {
      dataList['credential'] = credential
    } else if (token !== undefined && token !== '') {
      dataList['token'] = token
    } else if (
      clientId !== undefined &&
      clientId !== '' &&
      clientSecret !== undefined &&
      clientSecret !== ''
    ) {
      dataList['client_id'] = clientId
      dataList['client_secret'] = clientSecret
    } else {
      Object.assign(dataList, secretData.data)
      if (
        !selectedInstance ||
        !selectedInstance.metadata?.name ||
        !selectedInstance.metadata?.uid
      ) {
        throw Error(t('secrets.errorInvalidInstance'))
      }
      const { isInstanceEnabled } = useOrganization()
      if (isInstanceEnabled.value) {
        // isInstanceEnabled: instances list comes from Instance API
        // Need to distinguish Kafka (Instance) vs Pulsar (PulsarInstance)
        const { kafkaClustersFn } = useKafkaCluster()
        const hasKafkaClusters = kafkaClustersFn(selectedInstance.metadata.name).length > 0
        if (hasKafkaClusters) {
          // Kafka: use Instance UID directly
          ownerReferences.push({
            apiVersion: 'cloud.streamnative.io/v1alpha1',
            uid: selectedInstance.metadata.uid,
            name: selectedInstance.metadata.name,
            kind: 'Instance'
          })
        } else {
          // Pulsar: fetch PulsarInstance for correct UID
          const { data: pulsarInstance } = await useCloudApi().readNamespacedPulsarInstance(
            selectedInstance.metadata.name,
            organization
          )
          ownerReferences.push({
            apiVersion: 'cloud.streamnative.io/v1alpha1',
            uid: pulsarInstance.metadata?.uid ?? selectedInstance.metadata.uid,
            name: selectedInstance.metadata.name,
            kind: 'PulsarInstance'
          })
        }
      } else {
        // Not isInstanceEnabled: instances list comes from PulsarInstance API,
        // UID is already PulsarInstance UID
        ownerReferences.push({
          apiVersion: 'cloud.streamnative.io/v1alpha1',
          uid: selectedInstance.metadata.uid,
          name: selectedInstance.metadata.name,
          kind: 'PulsarInstance'
        })
      }
    }

    const binaryData = secretData.binaryData
    const body: V1alpha1SecretWithBinaryData = {
      data: Object.keys(dataList).length > 0 ? dataList : undefined,
      binaryData: Object.keys(binaryData).length > 0 ? binaryData : undefined,
      metadata: {
        name,
        namespace: organization,
        labels: catalog ? { 'cloud.streamnative.io/type': 'catalog' } : undefined
      },
      location: location,
      instanceName: selectedInstance?.metadata?.name
    }
    if (ownerReferences.length > 0) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore defined on line 31
      body.metadata.ownerReferences = ownerReferences
    }

    const createSecretResult = await useCloudApi().createNamespacedSecret(organization, body)

    if (!createSecretResult.data.metadata?.name) {
      throw Error('created secret is missing name')
    }

    const newSecret = {
      name: createSecretResult.data.metadata?.name,
      creationTimestamp: createSecretResult.data.metadata?.creationTimestamp,
      deletionTimestamp: undefined,
      instanceName: selectedInstance?.metadata?.name,
      keys: getSecretDataKeys(
        createSecretResult.data.data,
        (createSecretResult.data as V1alpha1SecretWithBinaryData).binaryData || binaryData
      ),
      data: createSecretResult.data.data || {},
      binaryData: (createSecretResult.data as V1alpha1SecretWithBinaryData).binaryData || binaryData
    }
    addSecret(newSecret)
    return newSecret
  } catch (e) {
    throw Error(getErrorMessage(e, 'createSecret Error'))
  }
}
const deleteSecret = async (secretName: string) => {
  const organization = usePulsarState().mustOrganization()
  await useCloudApi().deleteNamespacedSecret(secretName, organization)
  removeSecret(secretName)
}

const init = (initialState: PulsarState) => {
  const { organization } = usePulsarState()
  const { isRbacUpdating } = rbacHelper()

  const valueChanged = async ([org, ab]: [string | undefined, boolean | undefined]) => {
    if (!org) {
      secrets.value = []
      lastOrg = undefined
      return
    }
    if (ab) {
      return
    }

    if (org !== lastOrg) {
      const { canDescribeSecretList } = rbacManager()
      if (canDescribeSecretList()) {
        await getSecrets(org)
      }
    }
    lastOrg = org
  }

  watch([organization, isRbacUpdating], valueChanged)

  return valueChanged([initialState.organization, isRbacUpdating.value])
}

export const useSecret = () => {
  return {
    secrets,
    hasSecrets,
    secretNameList,
    deleteSecret,
    createSecret,
    getSecrets,
    getSecret,
    init
  }
}
